import React, { useState } from 'react';
import { Box, Card, TextField, Button, Typography, Container, Alert, CircularProgress } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCode, faLock, faEnvelope } from '@fortawesome/free-solid-svg-icons';
import apiService from '../services/api';
import { LoginRequest, User } from '../types';

interface LoginProps {
    onLogin: (token: string, user: User) => void;
}

export const Login: React.FC<LoginProps> = ({ onLogin }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const loginData: LoginRequest = {
                email,
                password
            };

            const response = await apiService.login(loginData);

            if (response.success) {
                const { token, user } = response.data;
                onLogin(token, user);
            } else {
                setError('Login failed. Please check your credentials.');
            }
        } catch (err: any) {
            console.error('Login error:', err);
            if (err.response?.data?.message) {
                setError(err.response.data.message);
            } else if (err.response?.status === 401) {
                setError('Invalid email or password.');
            } else if (err.response?.status === 422) {
                setError('Please check your input and try again.');
            } else {
                setError('An error occurred. Please try again later.');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'background.default',
                py: 3,
            }}
        >
            <Container component="main" maxWidth="xs">
                <Card 
                    sx={{ 
                        p: 4, 
                        width: '100%',
                        borderRadius: 3,
                        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
                    }}
                >
                    <Box sx={{ mb: 4, textAlign: 'center' }}>
                        <FontAwesomeIcon icon={faCode} size="3x" style={{ color: 'primary.main' }} />
                        <Typography component="h1" variant="h4" sx={{ mt: 2, fontWeight: 600 }}>
                            HSA-WEB
                        </Typography>
                        <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 1 }}>
                            HTML Viewer & Manager
                        </Typography>
                    </Box>

                    {error && (
                        <Alert severity="error" sx={{ mb: 3 }}>
                            {error}
                        </Alert>
                    )}

                    <Box component="form" onSubmit={handleSubmit}>
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            name="email"
                            label="Email Address"
                            type="email"
                            id="email"
                            autoComplete="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            disabled={loading}
                            InputProps={{
                                startAdornment: (
                                    <FontAwesomeIcon 
                                        icon={faEnvelope} 
                                        style={{ marginRight: '8px', color: 'rgba(0, 0, 0, 0.54)' }}
                                    />
                                ),
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                },
                            }}
                        />
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            name="password"
                            label="Password"
                            type="password"
                            id="password"
                            autoComplete="current-password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            disabled={loading}
                            InputProps={{
                                startAdornment: (
                                    <FontAwesomeIcon 
                                        icon={faLock} 
                                        style={{ marginRight: '8px', color: 'rgba(0, 0, 0, 0.54)' }}
                                    />
                                ),
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                },
                            }}
                        />
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            size="large"
                            disabled={loading}
                            sx={{ 
                                mt: 3,
                                mb: 2,
                                height: 48,
                                fontSize: '1rem',
                            }}
                        >
                            {loading ? (
                                <CircularProgress size={24} color="inherit" />
                            ) : (
                                'Sign In'
                            )}
                        </Button>
                    </Box>
                </Card>
            </Container>
        </Box>
    );
}; 